import { Column, Entity, Index, JoinColumn, ManyToOne, Unique } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { CrossReferenceRequestLineEntity } from './cross-reference-request-line.entity';
import { ItemEntity } from './item.entity';

/**
 * Provenance of the external identifier, mirroring
 * `ExternalArticleNumberKind` minus `internal` (an internal number is already
 * a catalog column — recording it here would duplicate the P1 index).
 *
 * The kind decides SCOPE: a `customer` number means "what THIS customer calls
 * this article" and is meaningless for anyone else, so it carries the client.
 * `competitor` and `norm` numbers are facts about the article itself and are
 * global.
 */
export type ItemCrossReferenceKind =
	| 'customer'
	| 'competitor'
	| 'norm'
	| 'unknown';

/**
 * A confirmed equivalence between an external identifier and one of our items
 * — the matching engine's LEARNING table, read by the P2
 * `known_cross_reference` pass.
 *
 * Every row is a decision somebody already made. That is why P2 outranks every
 * pass except a raw identifier hit: the engine is not inferring an equivalence
 * here, it is recalling one that a human confirmed.
 */
@Index('idx_icr_normalized', ['identifierNormalized'])
@Index('idx_icr_item', ['fkItemId'])
// The unique key is the (identity, scope, target) tuple, so re-confirming the
// same equivalence updates the existing row instead of accumulating duplicates.
//
// Caveat inherited from MySQL: NULLs compare as distinct in a unique index, so
// this constraint does NOT deduplicate GLOBAL (null-client) rows. Writers of
// global rows must look the row up before inserting rather than relying on the
// index; the constraint's real job is preventing per-client duplicates.
@Unique('uq_icr_identity', [
	'identifierNormalized',
	'identifierKind',
	'fkClientId',
	'fkItemId',
])
@Entity('item_cross_reference')
export class ItemCrossReferenceEntity extends BaseEntity {
	@Column('int', { name: 'fk_item__id', unsigned: true })
	public fkItemId!: number;

	/** The external identifier exactly as it was seen on the request line. */
	@Column('varchar', { name: 'identifierValue', length: 128 })
	public identifierValue!: string;

	/**
	 * The comparison key, folded with the SAME `normalizeIdentifier` the
	 * engine's P1 uses. One fold definition, never two — a second fold would
	 * silently stop half the postings from ever matching.
	 */
	@Column('varchar', { name: 'identifierNormalized', length: 128 })
	public identifierNormalized!: string;

	@Column('varchar', { name: 'identifierKind', length: 16 })
	public identifierKind!: ItemCrossReferenceKind;

	/**
	 * Client scope. Non-null rows only apply to that client's requests; null
	 * rows apply to everyone. See `ItemCrossReferenceKind` for which kinds
	 * carry a client.
	 */
	@Column('int', { name: 'fk_client__id', nullable: true, unsigned: true })
	public fkClientId!: number | null;

	/** 'review' (confirmed in the review queue) | 'manual' | 'import'. */
	@Column('varchar', { name: 'source', length: 32, default: 'review' })
	public source!: string;

	/** The request line whose confirmation created this equivalence. */
	@Column('int', {
		name: 'fk_cross_reference_request_line__id',
		nullable: true,
		unsigned: true,
	})
	public fkCrossReferenceRequestLineId!: number | null;

	@Column('int', { name: 'confirmedByUserId', nullable: true, unsigned: true })
	public confirmedByUserId!: number | null;

	@Column('datetime', { name: 'confirmedAt', nullable: true })
	public confirmedAt!: Date | null;

	/**
	 * Retiring a wrong equivalence is a deactivation, not a delete: the row is
	 * evidence of a decision somebody made and the audit trail needs it.
	 */
	@Column({ type: 'boolean', name: 'isActive', width: 1, default: () => "'1'" })
	public isActive!: boolean;

	@ManyToOne(() => ItemEntity, { onDelete: 'CASCADE', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_item__id', referencedColumnName: 'id' }])
	public fkItem!: ItemEntity;

	@ManyToOne(() => ClientEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity | null;

	// SET NULL rather than CASCADE: the equivalence outlives the request that
	// taught it. Losing the provenance pointer is acceptable; losing the learned
	// equivalence because a request was purged is not.
	@ManyToOne(() => CrossReferenceRequestLineEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([
		{ name: 'fk_cross_reference_request_line__id', referencedColumnName: 'id' },
	])
	public fkCrossReferenceRequestLine!: CrossReferenceRequestLineEntity | null;
}
